import { useEffect } from 'react';
// Named import (not default). The SDK's CJS output sets
// `exports.default = exports.Intercom`, but Vite/Rolldown's CJS→ESM
// interop in production builds doesn't expose `.default` as a callable
// — bundled code crashed with `(0, BX.default) is not a function` on
// dev-practice-v2 (the deployed build). `Intercom` is also exported as
// a named export on the same module, so this is equivalent at type-level
// and works through the bundler.
import { Intercom, shutdown, update } from '@intercom/messenger-js-sdk';

// Intercom should only boot when the app is talking to the production
// API. Local dev (empty BASE_URL) and the dev-env deploy
// (https://dev-api.scrips.com) both skip, so the chat widget never
// loads with test-user data and never pollutes the real Intercom
// workspace. `VITE_INTERCOM_FORCE=1` overrides for manual testing.
const IS_PROD_API =
  import.meta.env.VITE_API_BASE_URL === 'https://api.scrips.com';
import { useAuthStore } from '@/auth/auth-store';
import { INTERCOM_APP_ID } from '@/app/lib/sdk-constants';
import { SCRIPS_ADMIN_ROLE } from '@/auth/roles';

/**
 * Intercom messenger boot.
 *
 * Mirrors Flutter `menu_container_screen.dart:117-142`:
 *
 *   - Init with `appId='odr7pw7c'` (verbatim from Flutter line 118)
 *   - Skip entirely for super-admin role ('0') per Flutter line 116
 *   - Identify the user with email + name + companyId
 *
 * **`setUserHash` is INTENTIONALLY SKIPPED in React** — Flutter
 * `utils.dart:1166-1172` embeds the HMAC secret in the client bundle.
 * Safe in mobile (App Store binary), unsafe in web (view-source reveals
 * the key). Recommended follow-up: backend HMAC endpoint that signs the
 * authenticated user's email server-side. See `docs/CONVERSIONS/
 * menu-sdk-integrations/CONVERSION_NOTES.md` §6.
 *
 * `Intercom` is fire-and-forget; this component renders nothing.
 */

function capitalize(s: string | undefined | null): string {
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function IntercomIntegration() {
  const user = useAuthStore((s) => s.user);

  useEffect(() => {
    if (!user) return;

    // Skip Intercom for super-admin per Flutter line 116
    // (`if (loggedUserData?.role != scripsAdminRole)`)
    if (user.role === SCRIPS_ADMIN_ROLE) return;

    // Skip outside of prod-API environments unless explicitly forced —
    // keeps Intercom chat noise out of localhost AND the dev test env.
    if (!IS_PROD_API && !import.meta.env.VITE_INTERCOM_FORCE) return;

    const name =
      [capitalize(user.firstName), capitalize(user.lastName)]
        .filter(Boolean)
        .join(' ')
        .trim() || undefined;

    Intercom({
      app_id: INTERCOM_APP_ID,
      email: user.email,
      user_id: user.userId,
      name,
      company:
        // organizationName isn't on the current React UserData (would
        // need auth-store extension — shared file, deferred). Use
        // organizationId for `companyId` only; Intercom will resolve
        // the display name from its workspace settings if the company
        // is already registered there.
        user.organizationId
          ? {
              company_id: user.organizationId,
              id: user.organizationId,
            }
          : undefined,
      custom_attributes: {
        Role: user.role ?? '',
      },
      // NOTE: user_hash deliberately omitted — see file header.
    });

    return () => {
      shutdown();
    };
  }, [user]);

  // Update on user field changes without a full re-boot.
  useEffect(() => {
    if (!user || user.role === SCRIPS_ADMIN_ROLE) return;
    if (!IS_PROD_API && !import.meta.env.VITE_INTERCOM_FORCE) return;
    update({
      email: user.email,
      user_id: user.userId,
    });
  }, [user?.email, user?.userId, user?.role]);

  return null;
}
